/**
 * V2 activity ticker — V1's ambient life (components/activity-ticker.js):
 * on Discover, poll for new yoinks every 25s (the first poll only sets
 * the baseline), toast up to 3 per cycle ("Ada just yoinked Fixture
 * Beach", tap → the yoink), and celebrate community total milestones
 * (V1's thresholds + localStorage so each fires once per device).
 */
import * as gateway from '../../core/gateway.js';
import { queryPublicYoinks } from '../journal/journal.js';
import { showToast } from '../../ui/toast.js';

const POLL_MS = 25_000;
const MAX_PER_CYCLE = 3;
export const MILESTONES = [100, 250, 500, 1000, 2500, 5000, 7500, 10000, 15000, 20000, 25000, 50000, 75000, 100000];
const MILESTONE_KEY = 'yoink_last_milestone_count';

/**
 * LOWEST newly-crossed milestone (V1 activity-ticker.js:388 — a big
 * jump celebrates the next rung, not the top one). Exported for tests.
 */
export function crossedMilestone(total, lastCelebrated) {
  return MILESTONES.find((m) => total >= m && m > lastCelebrated) || null;
}

async function fetchLatest() {
  // Personal yoinks skip the ticker — same rule as the feed (§11)
  const { data, error } = await queryPublicYoinks({
    select: 'id,title,created_at,public_profiles(full_name,username),locations!inner(display_name)',
    order: 'created_at.desc,id.desc',
    limit: MAX_PER_CYCLE,
  });
  if (error) throw error;
  return data || [];
}

async function checkMilestone() {
  try {
    const { data: total, error } = await gateway.rawRpc('get_yoink_count_fast', { params: {} });
    if (error || typeof total !== 'number') return;
    let last = 0;
    try {
      last = Number(localStorage.getItem(MILESTONE_KEY)) || 0;
    } catch { /* private mode */ }
    const milestone = crossedMilestone(total, last);
    if (milestone) {
      try {
        localStorage.setItem(MILESTONE_KEY, String(total)); // V1 stores the COUNT
      } catch { /* private mode */ }
      showToast(`The journal just passed ${milestone.toLocaleString()} yoinks — thanks for exploring!`, {
        type: 'celebrate',
        duration: 9000,
      });
    }
  } catch {
    // celebration is optional
  }
}

/** Start the ticker; returns a stop function. */
export function startTicker({ router }) {
  let baseline = null; // newest id seen
  let stopped = false;

  async function poll() {
    if (stopped) return;
    try {
      const latest = await fetchLatest();
      if (stopped || !latest.length) return;
      if (baseline === null) {
        baseline = latest[0].id; // first poll baselines (V1)
        checkMilestone();
        return;
      }
      const fresh = [];
      for (const y of latest) {
        if (y.id === baseline) break;
        fresh.push(y);
      }
      if (!fresh.length) return;
      baseline = latest[0].id;
      // V1's 2-minute freshness window (activity-ticker.js:188-196) —
      // a backlog after a long sleep must not replay as "just yoinked"
      const twoMinutesAgo = Date.now() - 2 * 60 * 1000;
      const recent = fresh.filter((y) => new Date(y.created_at).getTime() >= twoMinutesAgo);
      recent.slice(0, MAX_PER_CYCLE).forEach((y, i) => {
        const who = y.public_profiles?.full_name || y.public_profiles?.username || 'Someone';
        const where = y.locations?.display_name || 'somewhere new';
        // 1.5s between queued toasts, like V1's queue pacing
        setTimeout(() => {
          if (stopped) return;
          showToast(`${who} just yoinked ${where}`, {
            onClick: () => router.navigate('yoink', { id: y.id }),
          });
        }, i * 1500);
      });
      checkMilestone();
    } catch {
      // quiet — the ticker must never disturb browsing
    }
  }

  poll();
  const interval = setInterval(poll, POLL_MS);
  return () => {
    stopped = true;
    clearInterval(interval);
  };
}
